import React from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { ThemeProvider } from "./lib/theme";
import Layout from "./components/Layout";
import Login from "./pages/Login";
import Overview from "./pages/Overview";
import Finance from "./pages/Finance";
import Banking from "./pages/Banking";
import Cash from "./pages/Cash";
import Reconciliation from "./pages/Reconciliation";
import Loans from "./pages/Loans";
import Receivables from "./pages/Receivables";
import Payables from "./pages/Payables";
import Projects from "./pages/Projects";
import ProjectDetail from "./pages/ProjectDetail";
import Inventory from "./pages/Inventory";
import Consumption from "./pages/Consumption";
import WorkbookIngest from "./pages/WorkbookIngest";
import ClientPortfolio from "./pages/ClientPortfolio";
import Client360 from "./pages/Client360";
import DataQualityReport from "./pages/DataQualityReport";
import EnrichMasterData from "./pages/EnrichMasterData";
import Equipment from "./pages/Equipment";
import HR from "./pages/HR";
import Compliance from "./pages/Compliance";
import Tenders from "./pages/Tenders";
import Risk from "./pages/Risk";
import Ratios from "./pages/Ratios";
import Growth from "./pages/Growth";
import AIAdvisor from "./pages/AIAdvisor";
import AccountLedger from "./pages/AccountLedger";
import ProjectBilling from "./pages/ProjectBilling";
import ProjectBillingDetail from "./pages/ProjectBillingDetail";
import Simulator from "./pages/Simulator";
import Intelligence from "./pages/Intelligence";
import Journals from "./pages/Journals";
import Ledger from "./pages/Ledger";
import Statements from "./pages/Statements";
import Audit from "./pages/Audit";
import Decisions from "./pages/Decisions";
import QuickEntry from "./pages/QuickEntry";
import Procurement from "./pages/Procurement";
import Sales from "./pages/Sales";
import Payroll from "./pages/Payroll";
import GSTDashboard from "./pages/GSTDashboard";
import BankImport from "./pages/BankImport";
import DocumentVault from "./pages/DocumentVault";
import AIAgents from "./pages/AIAgents";
import Analytics from "./pages/Analytics";
import Notifications from "./pages/Notifications";
import DataManager from "./pages/DataManager";
import Team from "./pages/Team";
import Settings from "./pages/Settings";
import TDSRetention from "./pages/TDSRetention";
import PnL from "./pages/PnL";
import ChequeRegister from "./pages/ChequeRegister";
import { Toaster } from "sonner";
import { can } from "./lib/rbac";

function RequireAuth({ children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

function RoleGuard({ route, children }) {
    const { user } = useAuth();
    if (!user) return <Navigate to="/login" replace />;
    if (!can(user.role, route)) return <Navigate to="/" replace />;
    return children;
}

function App() {
    return (
        <AuthProvider>
            <ThemeProvider>
            <BrowserRouter>
                <Routes>
                    <Route path="/login" element={<Login />} />
                    <Route element={<RequireAuth><Layout /></RequireAuth>}>
                        <Route path="/" element={<Overview />} />
                        <Route path="/finance" element={<Finance />} />
                        <Route path="/banking" element={<Banking />} />
                        <Route path="/cash" element={<Cash />} />
                        <Route path="/reconciliation" element={<Reconciliation />} />
                        <Route path="/loans" element={<Loans />} />
                        <Route path="/receivables" element={<Receivables />} />
                        <Route path="/payables" element={<Payables />} />
                        <Route path="/projects" element={<Projects />} />
                        <Route path="/projects/:id" element={<ProjectDetail />} />
                        <Route path="/inventory" element={<Inventory />} />
                        <Route path="/consumption" element={<Consumption />} />
                        <Route path="/workbook-ingest" element={<WorkbookIngest />} />
                        <Route path="/clients" element={<ClientPortfolio />} />
                        <Route path="/clients/:id" element={<Client360 />} />
                        <Route path="/data-quality" element={<DataQualityReport />} />
                        <Route path="/enrich" element={<EnrichMasterData />} />
                        <Route path="/equipment" element={<Equipment />} />
                        <Route path="/hr" element={<HR />} />
                        <Route path="/compliance" element={<Compliance />} />
                        <Route path="/tenders" element={<Tenders />} />
                        <Route path="/risk" element={<Risk />} />
                        <Route path="/ratios" element={<Ratios />} />
                        <Route path="/growth" element={<Growth />} />
                        <Route path="/ai-advisor" element={<AIAdvisor />} />
                        <Route path="/accounts/:kind/:accountId" element={<AccountLedger />} />
                        <Route path="/project-billing" element={<ProjectBilling />} />
                        <Route path="/project-billing/:workbookId" element={<ProjectBillingDetail />} />
                        <Route path="/simulator" element={<Simulator />} />
                        <Route path="/intelligence" element={<Intelligence />} />
                        <Route path="/journals" element={<Journals />} />
                        <Route path="/ledger" element={<Ledger />} />
                        <Route path="/statements" element={<Statements />} />
                        <Route path="/audit" element={<Audit />} />
                        <Route path="/decisions" element={<Decisions />} />
                        <Route path="/entry" element={<RoleGuard route="/entry"><QuickEntry /></RoleGuard>} />
                        <Route path="/procurement" element={<RoleGuard route="/procurement"><Procurement /></RoleGuard>} />
                        <Route path="/sales" element={<RoleGuard route="/sales"><Sales /></RoleGuard>} />
                        <Route path="/payroll" element={<RoleGuard route="/payroll"><Payroll /></RoleGuard>} />
                        <Route path="/gst" element={<RoleGuard route="/gst"><GSTDashboard /></RoleGuard>} />
                        <Route path="/bank-import" element={<RoleGuard route="/bank-import"><BankImport /></RoleGuard>} />
                        <Route path="/vault" element={<RoleGuard route="/vault"><DocumentVault /></RoleGuard>} />
                        <Route path="/agents" element={<RoleGuard route="/agents"><AIAgents /></RoleGuard>} />
                        <Route path="/analytics" element={<RoleGuard route="/analytics"><Analytics /></RoleGuard>} />
                        <Route path="/notifications" element={<RoleGuard route="/notifications"><Notifications /></RoleGuard>} />
                        <Route path="/data" element={<RoleGuard route="/data"><DataManager /></RoleGuard>} />
                        <Route path="/team" element={<RoleGuard route="/team"><Team /></RoleGuard>} />
                        <Route path="/settings" element={<RoleGuard route="/settings"><Settings /></RoleGuard>} />
                        <Route path="/tds-retention" element={<RoleGuard route="/tds-retention"><TDSRetention /></RoleGuard>} />
                        <Route path="/pnl" element={<RoleGuard route="/pnl"><PnL /></RoleGuard>} />
                        <Route path="/cheques" element={<RoleGuard route="/cheques"><ChequeRegister /></RoleGuard>} />
                    </Route>
                    <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
                <Toaster
                    position="top-right"
                    theme="dark"
                    toastOptions={{
                        style: {
                            background: "#FFFFFF",
                            border: "1px solid #E2E8F0",
                            borderRadius: 0,
                            fontFamily: "'IBM Plex Mono', monospace",
                            fontSize: "12px",
                            color: "#fff",
                        },
                    }}
                />
            </BrowserRouter>
            </ThemeProvider>
        </AuthProvider>
    );
}

export default App;
